import { Head, Link, router } from "@inertiajs/react"
import { Printer, Send } from "lucide-react"
import { Fragment, useEffect, useState } from "react"

import { AppShell } from "@/components/AppShell"
import { BidSubNav } from "@/components/bids/BidSubNav"
import { PRICING_FORMAT_LABELS } from "@/components/bids/BidStatusBadge"
import { HeaderColumn } from "@/components/print/PrintSheetHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { SegmentedControl } from "@/components/ui/segmented-control"
import { Select } from "@/components/ui/select"
import { money } from "@/lib/money"
import type { PricingFormat, SheetDocument, SheetPlanDoc, SheetProps } from "@/types/bids"

// The builder-facing bid sheet — what DRB receives: no costs, no margins,
// ever (the frozen payload carries zero cost keys, regression-tested).
// Renders a STORED revision verbatim; the live preview (before the first
// revision, or via the picker's "Live preview") carries a format toggle —
// the builder's setting is only the DEFAULT; each revision chooses its own
// at generate time (rate card first, full pricing after winning). Print
// rides the shared .print-chrome/.print-sheet stack — zero new print CSS.

export default function Sheet(props: SheetProps) {
  const { community, bid, revision, document, preview, revisions } = props
  const [busy, setBusy] = useState(false)
  // The preview's format choice; frozen revisions always render their stamp.
  const [format, setFormat] = useState<PricingFormat>(document.pricing_format)
  useEffect(() => setFormat(document.pricing_format), [document.pricing_format])
  const description = `Bid sheet for ${community.name} — ${bid.bid_number}.`

  // Generate freezes what you SEE: the toggled format in preview, the
  // stamped format when re-generating from a frozen revision's view.
  const shownFormat = preview ? format : document.pricing_format

  const generate = () => {
    setBusy(true)
    router.post(bid.urls.generate, { pricing_format: shownFormat },
      { preserveScroll: true, onFinish: () => setBusy(false) })
  }

  const markSent = () => {
    if (!revision) return
    setBusy(true)
    router.post(revision.mark_sent_url, {}, { preserveScroll: true, onFinish: () => setBusy(false) })
  }

  const roomLevel = shownFormat === "room_level_totals"

  return (
    <>
      <Head title={`${community.name} · Bid sheet`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${community.name} · Bid sheet`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="print-chrome">
          <p className="text-sm text-ink-muted">
            <Link href="/bids" className="hover:text-ink-body">
              Bids
            </Link>{" "}
            /{" "}
            <Link href={bid.urls.workspace} className="hover:text-ink-body">
              {community.name}
            </Link>{" "}
            / <strong>Bid sheet</strong>
          </p>
          <BidSubNav
            active="sheet"
            urls={{ workspace: bid.urls.workspace, matrix: bid.urls.matrix, sheet: bid.urls.sheet }}
          />

          <div className="mb-4 mt-4 flex flex-wrap items-center gap-3">
            {revisions.length > 0 && (
              <Select
                aria-label="Revision"
                className="w-40"
                value={revision ? String(revision.number) : "preview"}
                onChange={(e) => router.get(bid.urls.sheet, { rev: e.target.value })}
                data-testid="sheet-rev-picker"
              >
                <option value="preview">Live preview</option>
                {revisions.map((r) => (
                  <option key={r.number} value={r.number}>
                    Rev {r.number}
                    {r.sent_at ? " · sent" : ""}
                  </option>
                ))}
              </Select>
            )}
            {preview && (
              <>
                <Badge tone="signal" data-testid="sheet-preview-badge">
                  {revisions.length > 0 ? "Preview — not frozen" : "Preview — nothing frozen yet"}
                </Badge>
                <span data-testid="sheet-format-toggle">
                  <SegmentedControl
                    value={format}
                    onChange={(value) => setFormat(value as PricingFormat)}
                    options={[
                      { value: "room_level_totals", label: PRICING_FORMAT_LABELS.room_level_totals },
                      { value: "sf_price_only", label: PRICING_FORMAT_LABELS.sf_price_only },
                    ]}
                  />
                </span>
              </>
            )}
            <span className="ml-auto flex gap-2">
              <Button
                variant="ghost"
                onClick={generate}
                disabled={busy}
                data-testid="generate-revision"
              >
                Generate revision
              </Button>
              {revision && (
                <Button
                  variant="ghost"
                  onClick={markSent}
                  disabled={busy || revision.sent_at != null}
                  data-testid="mark-sent"
                >
                  <Send className="mr-1.5 h-4 w-4" />
                  {revision.sent_at ? "Sent" : "Mark sent"}
                </Button>
              )}
              <Button variant="soft" onClick={() => window.print()} data-testid="print-bid-sheet">
                <Printer className="mr-1.5 h-4 w-4" /> Print / Save as PDF
              </Button>
            </span>
          </div>
        </div>

        <article
          className="print-sheet mx-auto max-w-4xl rounded-lg border border-hairline bg-surface p-6"
          data-testid="bid-sheet"
        >
          <header className="flex flex-wrap items-start justify-between gap-3 border-b border-hairline pb-3">
            <div>
              <h1>Flooring Bid</h1>
              <p className="text-sm text-ink-muted">Floor Authority</p>
            </div>
            <div className="text-right text-sm">
              <p className="font-medium text-ink-display">
                {document.bid_number}
                {document.revision != null && ` · Rev ${document.revision}`}
              </p>
              <p className="text-ink-muted">
                {document.generated_at
                  ? `Generated ${new Date(document.generated_at).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}`
                  : "Not yet generated"}{" "}
                · Valid {document.valid_days} days
              </p>
            </div>
          </header>

          <div className="mt-4 grid gap-x-8 gap-y-1 sm:grid-cols-2">
            <HeaderColumn
              pairs={[
                ["Prepared for", document.prepared_for || document.customer.name],
                ["Community", document.community.name],
                ["Locality", document.community.locality],
              ]}
            />
            <HeaderColumn
              pairs={[
                [
                  "Pricing basis",
                  roomLevel
                    ? `Per room & level, ${document.pricing_basis === "waste_in" ? "waste included" : "net quantities"}`
                    : "Rate card — one price per level per material",
                ],
                ["Bid", document.bid_number],
              ]}
            />
          </div>

          {roomLevel ? (
            document.plans.length === 0 ? (
              <div className="callout mt-6">
                <p>
                  Nothing priced yet — sync a plan&apos;s quantities and pick a base bid in the
                  matrix.
                </p>
              </div>
            ) : (
              document.plans.map((plan) => <PlanSection key={plan.plan_id} plan={plan} />)
            )
          ) : (
            <RateCard document={document} />
          )}

          <footer className="mt-6 border-t border-hairline pt-2 text-xs text-ink-muted">
            Prices include material, labor, setting materials and applicable waste per the level
            schedule. Quantities measured from architectural plans; final billing per contracted
            plan quantities. Floor Authority · {document.bid_number}
            {document.revision != null && ` Rev ${document.revision}`}.
          </footer>
        </article>
      </AppShell>
    </>
  )
}

function PlanSection({ plan }: { plan: SheetPlanDoc }) {
  return (
    <section className="mt-6" data-testid={`sheet-plan-${plan.plan_id}`}>
      <h2>
        {plan.name}
        {plan.heated_sf != null && (
          <span className="ml-2 text-sm font-normal text-ink-muted">
            {plan.heated_sf.toLocaleString()} heated SF
          </span>
        )}
      </h2>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-ink-muted text-left align-bottom text-xs uppercase tracking-wide text-ink-muted">
              <th className="px-1.5 pb-1 font-semibold">Location</th>
              <th className="px-1.5 pb-1 text-right font-semibold">Quantity</th>
              <th className="px-1.5 pb-1 font-semibold">Unit</th>
              <th className="px-1.5 pb-1 text-right font-semibold">Price</th>
              <th className="px-1.5 pb-1 text-right font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            {plan.sections.map((section) => (
              <Fragment key={`${section.category}-${section.level_name}`}>
                <tr className="schedule-row border-b border-hairline bg-page">
                  <td className="px-1.5 py-1.5 font-medium text-ink-display" colSpan={5}>
                    {section.category_label} — {section.level_name}
                  </td>
                </tr>
                {section.rows.map((row, index) => (
                  <tr key={index} className="schedule-row border-b border-hairline">
                    <td className="px-1.5 py-1.5">{row.location}</td>
                    <td className="px-1.5 py-1.5 text-right tabular-nums">{row.qty}</td>
                    <td className="px-1.5 py-1.5">{row.unit}</td>
                    <td className="px-1.5 py-1.5 text-right tabular-nums">{money(row.price)}</td>
                    <td className="px-1.5 py-1.5 text-right tabular-nums">{money(row.total)}</td>
                  </tr>
                ))}
                <tr className="schedule-row border-b border-hairline text-sm font-medium">
                  <td className="px-1.5 py-1.5" colSpan={4}>
                    {section.category_label} {section.level_name} total
                  </td>
                  <td className="px-1.5 py-1.5 text-right tabular-nums">
                    {money(section.section_total)}
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-ink-muted text-sm font-semibold">
              <td className="px-1.5 py-2" colSpan={4}>
                Total — {plan.name}
              </td>
              <td
                className="px-1.5 py-2 text-right tabular-nums"
                data-testid={`plan-total-${plan.plan_id}`}
              >
                {money(plan.plan_total)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  )
}

function RateCard({ document }: { document: SheetDocument }) {
  if (document.rate_card.length === 0) {
    return (
      <div className="callout mt-6">
        <p>No priced levels yet — add levels in the workspace first.</p>
      </div>
    )
  }
  return (
    <section className="mt-6" data-testid="rate-card">
      <h2>Level schedule</h2>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-ink-muted text-left align-bottom text-xs uppercase tracking-wide text-ink-muted">
              <th className="px-1.5 pb-1 font-semibold">Material</th>
              <th className="px-1.5 pb-1 font-semibold">Level</th>
              <th className="px-1.5 pb-1 text-right font-semibold">Price</th>
            </tr>
          </thead>
          <tbody>
            {document.rate_card.map((category) =>
              category.levels.map((level, index) => (
                <tr
                  key={`${category.category}-${level.name}`}
                  className="schedule-row border-b border-hairline"
                >
                  <td className="px-1.5 py-1.5 font-medium text-ink-display">
                    {index === 0 ? category.category_label : ""}
                  </td>
                  <td className="px-1.5 py-1.5">{level.name}</td>
                  <td className="px-1.5 py-1.5 text-right tabular-nums">
                    {money(level.rate)}/{level.unit}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-ink-muted">
        Quantities per your plan takeoffs; one price per level per material, applied to every plan
        in {document.community.name}.
      </p>
    </section>
  )
}
